// 用户模块API接口

import { BaseApi } from "../../../utils/request";
import { User } from "../types";
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ReLoadUserResponse,
} from "../types/auth.types";
import { Currency } from "../../startup/types";

// 用户API
export class UserApi extends BaseApi {
  // 获取用户信息
  // 不做 api 层缓存：缓存统一由 service 层 staticUserCache(15分钟)+在途去重管理。
  // 否则 clearUserInfoCache 清不掉这层，编辑/身份变更后的"立即刷新"会被旧响应挡住。
  async getUserInfo(): Promise<User> {
    const response = await this.post<ReLoadUserResponse>(
      "/api/v1/user_info",
      {},
      { alert: false }
    );
    return response.data.data;
  }

  // 获取用户实时态（余额/任务奖励/充值次数等高频字段）。不缓存：领奖/充值后需要新鲜数据。
  async getUserRealtime(): Promise<Partial<User>> {
    const response = await this.post<ReLoadUserResponse>(
      "/api/v1/user_realtime",
      {},
      { alert: false }
    );
    return response.data.data;
  }

  // 获取红包状态（redpacket / redPacketReceivable）。不缓存。
  async getRedpacketStatus(): Promise<Partial<User>> {
    const response = await this.post<ReLoadUserResponse>(
      "/api/v1/redpacket_status",
      {},
      { alert: false }
    );
    return response.data.data;
  }

  // 获取签到状态（bonusTaskCheckInDay / userCheckIn）。不缓存。
  async getUserCheckin(): Promise<Partial<User>> {
    const response = await this.post<ReLoadUserResponse>(
      "/api/v1/user_checkin",
      {},
      { alert: false }
    );
    return response.data.data;
  }

  // 获取游客信息
  async getGuestInfo(): Promise<User> {
    const response = await this.post<ReLoadUserResponse>(
      "/api/v1/guest_info",
      {},
      {
        cache: true,
        cacheTime: 5 * 60 * 1000, // 5分钟缓存
        alert: false,
      }
    );
    return response.data.data;
  }

  // 用户登录
  async login(data: LoginRequest): Promise<LoginResponse> {
    let url = "/api/auth/login";
    if (data.loginType === "mobile") {
      url = "/api/auth/phone_login";
    }
    const response = await this.post<LoginResponse>(url, data, {
      alert: false,
    });
    return response.data;
  }

  // 用户注册
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await this.post<RegisterResponse>(
      "/api/auth/register",
      data,
      {
        alert: false,
      }
    );
    return response.data;
  }

  // 更新用户信息
  async updateUserInfo(data: Partial<User>): Promise<User> {
    const response = await this.put<User>("/api/user/info", data);
    return response.data;
  }

  // 获取用户余额
  async getUserBalance(): Promise<{ balance: number; currency: string }> {
    const response = await this.get<{ balance: number; currency: string }>(
      "/api/user/balance",
      {},
      {
        cache: true,
        cacheTime: 30 * 1000, // 30秒缓存
        alert: false,
      }
    );
    return response.data;
  }

  // 获取货币列表
  async getCurrencyList(): Promise<{
    list: Currency[];
    fiatId?: string;
    hideCrypto?: boolean;
    tokenItems?: any[];
    networks?: any[];
  }> {
    const response = await this.get<{
      list: Currency[];
      fiatId?: string;
      hideCrypto?: boolean;
      tokenItems?: any[];
      networks?: any[];
    }>(
      "/api/auth/account_list",
      {},
      {
        cache: true,
        cacheTime: 10 * 60 * 1000, // 10分钟缓存
        alert: false,
      }
    );
    return response.data;
  }
}

// 创建API实例
export const userApi = new UserApi();
